import {
  CardGridSkeleton,
  CardListSkeleton,
} from "@/components/Common/SkeletonLoading";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  SquareActivity,
  Stethoscope,
  Ticket,
  Wallet,
} from "lucide-react";

import { getPermissions } from "@/common/Permissions";
import CreateEncounterForm from "@/components/Encounter/CreateEncounterForm";
import { canUseFullPatientActions } from "@/components/Patient/patientActionVisibility";
import { PatientInfoCard } from "@/components/Patient/PatientInfoCard";
import RegistrationChargeStatusCard from "@/components/Patient/RegistrationChargeStatusCard";
import CreateTokenForm from "@/components/Tokens/CreateTokenForm";
import PatientTokensList from "@/components/Tokens/PatientTokensList";
import { Button } from "@/components/ui/button";
import { usePermissions } from "@/context/PermissionContext";
import { useShortcutSubContext } from "@/context/ShortcutContext";
import useAppHistory from "@/hooks/useAppHistory";
import useAuthUser from "@/hooks/useAuthUser";
import useBreakpoints from "@/hooks/useBreakpoints";
import BookAppointmentSheet from "@/pages/Appointments/BookAppointment/BookAppointmentSheet";
import { QuickAction } from "@/pages/Encounters/tabs/overview/quick-actions";
import {
  getEncounterDiscoveryCapabilities,
  getEncounterWorkAreaCapabilities,
} from "@/pages/Encounters/utils/encounterPermissions";
import useCurrentFacility from "@/pages/Facility/utils/useCurrentFacility";
import { PLUGIN_Component } from "@/PluginEngine";
import { ChargeItemServiceResource } from "@/types/billing/chargeItem/chargeItem";
import chargeItemApi from "@/types/billing/chargeItem/chargeItemApi";
import { InvoiceStatus } from "@/types/billing/invoice/invoice";
import patientApi from "@/types/emr/patient/patientApi";
import query from "@/Utils/request/query";
import careConfig from "@careConfig";
import { useQueryParams } from "raviger";
import { useTranslation } from "react-i18next";
import PatientHomeTabs from "./home/PatientHomeTabs";

export default function VerifyPatient() {
  useShortcutSubContext("facility:patient:home");
  const { t } = useTranslation();
  // practitioner workspace is derived from facility permissions
  const [qParams] = useQueryParams();
  const queryClient = useQueryClient();

  const { patient_id, phone_number, year_of_birth, partial_id } = qParams;
  const { goBack } = useAppHistory();
  const { facility, facilityId } = useCurrentFacility();

  const { hasPermission } = usePermissions();
  const user = useAuthUser();
  const isTab = useBreakpoints({ default: true, lg: false });
  const hasDirectPatientId = !!patient_id;

  const facilityPermissions = getPermissions(
    hasPermission,
    facility?.permissions ?? [],
  );
  const { canWriteAppointment, canWriteToken, canListTokens, canReadAccount } =
    facilityPermissions;
  const { canCreateEncounter, canViewEncounterHistory } =
    getEncounterDiscoveryCapabilities(facilityPermissions);
  const { canViewEncounterWorkArea, canManageEncounterActions } =
    getEncounterWorkAreaCapabilities(facilityPermissions);
  const canUseFullActions = canUseFullPatientActions({
    isPractitionerWorkspace: facilityPermissions.canAccessPractitionerWorkspace,
    canAccessNursingActions: facilityPermissions.canAccessNursingActions,
    isSuperuser: user?.is_superuser,
  });

  const {
    data: patientData,
    isPending: isVerifyingPatient,
    isError,
  } = useQuery({
    queryKey: [
      "patient-verify",
      patient_id ?? null,
      phone_number,
      year_of_birth,
      partial_id,
      facilityId ?? null,
    ],
    queryFn: hasDirectPatientId
      ? query(patientApi.get, {
          pathParams: { id: patient_id },
          queryParams: facilityId ? { facility: facilityId } : undefined,
          silent: true,
        })
      : query(patientApi.searchRetrieve, {
          body: { phone_number: phone_number ?? "", year_of_birth, partial_id },
          queryParams: facilityId ? { facility: facilityId } : undefined,
        }),
    enabled: hasDirectPatientId || !!(year_of_birth && partial_id),
  });

  const registrationChargeDefinition =
    facility?.registration_charge_item_definition;
  const {
    data: registrationChargeData,
    isPending: isLoadingRegistrationCharge,
  } = useQuery({
    queryKey: ["registration-charge", facilityId, patientData?.id],
    queryFn: query(chargeItemApi.listChargeItem, {
      pathParams: { facilityId },
      queryParams: {
        patient: patientData?.id || "",
        limit: 1,
        service_resource: ChargeItemServiceResource.patient_registration,
        service_resource_id: patientData?.id || "",
      },
    }),
    enabled: !!registrationChargeDefinition && !!patientData?.id,
  });

  const registrationChargeItem = registrationChargeData?.results[0];
  const hasUnpaidRegistrationCharge =
    !!registrationChargeItem &&
    registrationChargeItem.paid_invoice?.status !== InvoiceStatus.balanced;
  const canShowScheduleAppointment =
    canWriteAppointment &&
    (!registrationChargeDefinition ||
      (!isLoadingRegistrationCharge && !hasUnpaidRegistrationCharge));
  const canViewAccounts = canReadAccount;

  // Auto-open the booking sheet if the user is returning from billing after
  // creating a pending OPD appointment that still needs to be confirmed.
  const pendingBookingStorageKey = `opd_pending_booking:${facilityId}:${patientData?.id}`;
  const hasPendingBooking =
    !!patientData?.id && !!sessionStorage.getItem(pendingBookingStorageKey);

  if (isVerifyingPatient || !facility) {
    return (
      <div className="space-y-4 md:max-w-5xl mx-auto">
        <CardListSkeleton count={1} />
        <CardGridSkeleton count={4} />
      </div>
    );
  }
  return (
    <div>
      {!year_of_birth || !partial_id ? (
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertDescription>
            {t("missing_required_params_for_patient_verification")}
          </AlertDescription>
        </Alert>
      ) : patientData ? (
        <div className="space-y-6 md:max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="space-y-6 lg:col-span-2">
              <div className="">
                <PatientInfoCard
                  tags={patientData.instance_tags}
                  tagEntityType="patient"
                  tagEntityId={patientData.id}
                  patient={patientData}
                  facilityId={facilityId}
                  onTagsUpdate={() => {
                    queryClient.invalidateQueries({
                      queryKey: [
                        "patient-verify",
                        phone_number,
                        year_of_birth,
                        partial_id,
                      ],
                    });
                  }}
                >
                  <PLUGIN_Component
                    __name="PatientInfoCardActions"
                    patient={patientData}
                    facilityId={facilityId}
                    className="flex justify-end"
                  />
                </PatientInfoCard>
              </div>

              <div className="grid gap-4 grid-cols-2  lg:grid-cols-3">
                {canCreateEncounter && canUseFullActions && (
                  <CreateEncounterForm
                    patientId={patientData.id}
                    facilityId={facilityId}
                    patientName={patientData.name}
                    disableRedirectOnSuccess={!canViewEncounterWorkArea}
                    trigger={
                      <QuickAction
                        icon={<SquareActivity className="text-orange-500" />}
                        title={t("create_encounter")}
                        actionId="create-encounter"
                      />
                    }
                  />
                )}

                {canShowScheduleAppointment && canUseFullActions && (
                  <BookAppointmentSheet
                    patientId={patientData.id}
                    facilityId={facilityId}
                    defaultOpen={hasPendingBooking}
                    trigger={
                      <QuickAction
                        icon={<Stethoscope className="text-purple-500" />}
                        title={t("schedule_appointment")}
                        actionId="schedule-appointment"
                      />
                    }
                  />
                )}

                {canWriteToken &&
                  careConfig.enableTokenGenerationInPatientHome && (
                    <CreateTokenForm
                      patient={patientData}
                      facilityId={facilityId}
                      trigger={
                        <QuickAction
                          icon={<Ticket className="text-gray-500" />}
                          title={t("generate_token")}
                          actionId="generate-token"
                        />
                      }
                    />
                  )}

                {canViewAccounts && canUseFullActions && (
                  <QuickAction
                    icon={<Wallet className="size-4" />}
                    title={t("view_accounts")}
                    actionId="view-the-accounts"
                    href={`/facility/${facilityId}/billing/account?status=active&patient_filter=${patientData.id}&patient_name=${patientData.name}`}
                  />
                )}
              </div>

              {canUseFullActions && (
                <RegistrationChargeStatusCard
                  facility={facility}
                  facilityId={facilityId}
                  patientId={patientData.id}
                />
              )}

              <PatientHomeTabs
                patientId={patientData.id}
                facility={facility}
                facilityPermissions={facility?.permissions ?? []}
                canListEncounters={canViewEncounterHistory}
                canOpenEncounters={canViewEncounterWorkArea}
                canManageEncounterActions={canManageEncounterActions}
                canWriteAppointment={canWriteAppointment}
                canListTokens={canListTokens}
              />
            </div>

            <div className="space-y-4">
              {canListTokens && !isTab && (
                <PatientTokensList
                  patientId={patientData.id}
                  facility={facility}
                />
              )}
            </div>
          </div>
        </div>
      ) : (
        isError && (
          <div className="h-screen w-full flex items-center justify-center">
            <div className="flex flex-col items-center justify-center text-center">
              <h3 className="text-xl font-semibold mb-1">
                {t("verification_failed")}
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                {t("please_enter_correct_birth_year")}
              </p>
              <Button
                variant={"primary_gradient"}
                className="gap-3 group"
                onClick={() => goBack(`/facility/${facilityId}/patients`)}
              >
                {t("go_back")}
              </Button>
            </div>
          </div>
        )
      )}
    </div>
  );
}
